import { createServerFn } from "@tanstack/react-start";
import { customInstance } from "@/lib/api/custom-instance";
import { logError, logInfo } from "@/lib/structured-log";

export interface BlacklistEntry {
  id: string;
  workspace_id: string;
  company_name: string;
  website: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlacklistListResponse {
  items: BlacklistEntry[];
  total: number;
  page: number;
  page_size: number;
}

interface CreateBlacklistData {
  workspaceId: string;
  company_name: string;
  website?: string | null;
}

interface UpdateBlacklistData {
  workspaceId: string;
  blacklistId: string;
  company_name?: string;
  website?: string | null;
}

interface DeleteBlacklistData {
  workspaceId: string;
  blacklistId: string;
}

interface ListBlacklistsData {
  workspaceId: string;
  page?: number;
  pageSize?: number;
}

export const createBlacklistServer = createServerFn({ method: "POST" })
  .validator((data: CreateBlacklistData) => data)
  .handler(async ({ data }): Promise<BlacklistEntry> => {
    const { workspaceId, company_name, website } = data;

    try {
      logInfo("blacklist.create.start", {
        workspace_id: workspaceId,
        company_name,
      });

      const res = await customInstance<{ data: BlacklistEntry; status: number }>(
        `/api/workspaces/${workspaceId}/blacklist`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            company_name,
            website: website || null,
          }),
        },
      );

      if (res.status !== 201) {
        logError("blacklist.create.non_ok", {
          status: res.status,
          workspace_id: workspaceId,
          company_name,
        });
        throw new Error(`Failed to create blacklist entry: ${res.status}`);
      }

      const entry = res.data;

      logInfo("blacklist.create.success", {
        workspace_id: workspaceId,
        blacklist_id: entry.id,
      });

      return entry;
    } catch (error) {
      logError("blacklist.create.error", {
        workspace_id: workspaceId,
        company_name,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export const listBlacklistsServer = createServerFn({ method: "GET" })
  .validator((data: ListBlacklistsData) => data)
  .handler(async ({ data }): Promise<BlacklistListResponse> => {
    const { workspaceId, page = 1, pageSize = 20 } = data;

    try {
      logInfo("blacklist.list.start", {
        workspace_id: workspaceId,
        page,
        page_size: pageSize,
      });

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      const res = await customInstance<{ data: BlacklistListResponse; status: number }>(
        `/api/workspaces/${workspaceId}/blacklist?${params}`,
        {
          method: "GET",
        },
      );

      if (res.status !== 200) {
        logError("blacklist.list.non_ok", {
          status: res.status,
          workspace_id: workspaceId,
          page,
          page_size: pageSize,
        });
        throw new Error(`Failed to list blacklist entries: ${res.status}`);
      }

      const result = res.data;

      logInfo("blacklist.list.success", {
        workspace_id: workspaceId,
        page: result.page,
        page_size: result.page_size,
        total: result.total,
        items_count: result.items.length,
      });

      return result;
    } catch (error) {
      logError("blacklist.list.error", {
        workspace_id: workspaceId,
        page,
        page_size: pageSize,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export const updateBlacklistServer = createServerFn({ method: "POST" })
  .validator((data: UpdateBlacklistData) => data)
  .handler(async ({ data }): Promise<BlacklistEntry> => {
    const { workspaceId, blacklistId, company_name, website } = data;

    try {
      logInfo("blacklist.update.start", {
        workspace_id: workspaceId,
        blacklist_id: blacklistId,
      });

      const res = await customInstance<{ data: BlacklistEntry; status: number }>(
        `/api/workspaces/${workspaceId}/blacklist/${blacklistId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            company_name,
            website,
          }),
        },
      );

      if (res.status !== 200) {
        logError("blacklist.update.non_ok", {
          status: res.status,
          workspace_id: workspaceId,
          blacklist_id: blacklistId,
        });
        throw new Error(`Failed to update blacklist entry: ${res.status}`);
      }

      const entry = res.data;

      logInfo("blacklist.update.success", {
        workspace_id: workspaceId,
        blacklist_id: entry.id,
      });

      return entry;
    } catch (error) {
      logError("blacklist.update.error", {
        workspace_id: workspaceId,
        blacklist_id: blacklistId,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export const deleteBlacklistServer = createServerFn({ method: "POST" })
  .validator((data: DeleteBlacklistData) => data)
  .handler(async ({ data }): Promise<void> => {
    const { workspaceId, blacklistId } = data;

    try {
      logInfo("blacklist.delete.start", {
        workspace_id: workspaceId,
        blacklist_id: blacklistId,
      });

      const res = await customInstance<{ status: number }>(
        `/api/workspaces/${workspaceId}/blacklist/${blacklistId}`,
        {
          method: "DELETE",
        },
      );

      if (res.status !== 204) {
        logError("blacklist.delete.non_ok", {
          status: res.status,
          workspace_id: workspaceId,
          blacklist_id: blacklistId,
        });
        throw new Error(`Failed to delete blacklist entry: ${res.status}`);
      }

      logInfo("blacklist.delete.success", {
        workspace_id: workspaceId,
        blacklist_id: blacklistId,
      });
    } catch (error) {
      logError("blacklist.delete.error", {
        workspace_id: workspaceId,
        blacklist_id: blacklistId,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export interface BatchCreateBlacklistData {
  workspaceId: string;
  items: {
    company_name: string;
    website?: string | null;
  }[];
}

export const batchCreateBlacklistServer = createServerFn({ method: "POST" })
  .validator((data: BatchCreateBlacklistData) => data)
  .handler(async ({ data }): Promise<{ count: number }> => {
    const { workspaceId, items } = data;

    try {
      logInfo("blacklist.batch_create.start", {
        workspace_id: workspaceId,
        count: items.length,
      });

      const res = await customInstance<{ data: { count: number }; status: number }>(
        `/api/workspaces/${workspaceId}/blacklist/batch`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items,
          }),
        },
      );

      if (res.status !== 201) {
        logError("blacklist.batch_create.non_ok", {
          status: res.status,
          workspace_id: workspaceId,
          count: items.length,
        });
        throw new Error(`Failed to batch create blacklist entries: ${res.status}`);
      }

      const result = res.data;

      logInfo("blacklist.batch_create.success", {
        workspace_id: workspaceId,
        count: result.count,
      });

      return result;
    } catch (error) {
      logError("blacklist.batch_create.error", {
        workspace_id: workspaceId,
        count: items.length,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });
